import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { generateSwitchList, getTrackCars, getWorkOrders } from '../api/switchlists'
import { dispatchWorkOrder } from '../api/workorders'
import { getRoutableTracks } from '../api/tracks'
import { getAvailableLocomotives } from '../api/locomotives'
import { getActiveCrews } from '../api/crews'
import { getSpotsForTrack } from '../api/spots'
import type { SwitchListGenerateRequest } from '../types/switchlist'
import type { WorkOrderDispatchRequest } from '../types/interchange'

/**
 * Execution-context React Query hooks (ADR-002/ADR-007) — switch job
 * builder + work order queue. Server state lives here, keyed on
 * `facilityId` for tenant isolation; never copy results into stores.
 */

export const executionKeys = {
  trackCars: (facilityId: string, trackId: string) =>
    ['track-cars', facilityId, trackId] as const,
  workOrders: (facilityId: string, status?: string[]) =>
    ['workorders', facilityId, ...(status ?? [])] as const,
  routableTracks: (facilityId: string) => ['routable-tracks', facilityId] as const,
  locomotives: (facilityId: string) => ['locomotives', facilityId] as const,
  crews: () => ['crews'] as const,
  trackSpots: (trackId: string) => ['track-spots', trackId] as const,
}

/** GET /api/v1/yards/{facilityId}/tracks/{trackId}/cars — ordered A-end → B-end. */
export function useTrackCarsQuery(facilityId: string | undefined, trackId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.trackCars(facilityId ?? '', trackId ?? ''),
    queryFn: () => getTrackCars(facilityId!, trackId!),
    enabled: !!facilityId && !!trackId,
  })
}

/** GET /api/v1/workorders — the switch job queue. */
export function useWorkOrdersQuery(
  facilityId: string | undefined,
  status?: string[],
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: executionKeys.workOrders(facilityId ?? '', status),
    queryFn: () => getWorkOrders(facilityId, status),
    enabled: !!facilityId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
  })
}

/** GET /api/v1/tracks/routable — source/destination pickers. */
export function useRoutableTracksQuery(facilityId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.routableTracks(facilityId ?? ''),
    queryFn: () => getRoutableTracks(facilityId!),
    enabled: !!facilityId,
  })
}

/** GET /api/v1/locomotives/available — locomotive picker. */
export function useAvailableLocomotivesQuery(facilityId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.locomotives(facilityId ?? ''),
    queryFn: () => getAvailableLocomotives(facilityId!),
    enabled: !!facilityId,
  })
}

/** GET /api/crews/active — crew picker (pre-v1 path, existing drift). */
export function useActiveCrewsQuery() {
  return useQuery({
    queryKey: executionKeys.crews(),
    queryFn: getActiveCrews,
  })
}

/** GET /api/spots?trackId= — destination spot picker (spur tracks). */
export function useTrackSpotsQuery(trackId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.trackSpots(trackId ?? ''),
    queryFn: () => getSpotsForTrack(trackId!),
    enabled: !!trackId,
  })
}

/** POST /api/v1/switchlists/generate — preview + dispatch-time revalidation. */
export function useGenerateSwitchListMutation(facilityId: string) {
  return useMutation({
    mutationFn: (payload: Omit<SwitchListGenerateRequest, 'facilityId'>) =>
      generateSwitchList({ ...payload, facilityId }),
  })
}

/** POST /api/v1/workorders/dispatch — invalidates the queue on success. */
export function useDispatchWorkOrderMutation(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: WorkOrderDispatchRequest) => dispatchWorkOrder(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workorders', facilityId] }),
  })
}
